import { useId, useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { LineColumnDef } from '@project/contracts'
import {
  isLineCellDashed, isLineCellOpen, isLineCellVisible, ZERO_MONEY, addMoney, addQty, addRate, lineAmount, lookupOwners, money, pct, qty, rate, subQty, type Money as MoneyValue,
} from '@project/contracts'
import { Button, Icon, Lookup, Money, Select, Table, type ColumnDef as TableColumn } from '../design/index.js'
import { useApi, type LineRow } from '../app/api.js'
import { asDecimalString, asText, formatDate, formatQuantity, formatRate } from './format.js'
import { LineDetail } from './LineDetail.js'
import { slabOf, slabPatch, slabText, useGstSlabs } from './tax-slabs.js'

/** A row of another grid, offered by a picker column. */
export interface PickableRow {
  readonly value: string
  readonly label: string
}

export interface LineGridProps {
  readonly columns: readonly LineColumnDef[]
  readonly lines: readonly LineRow[]
  readonly readOnly: boolean
  readonly onChange: (lines: readonly LineRow[]) => void
  /**
   * Which storage section a row added here belongs to.
   *
   * Set on every new row, because the server files a row with no section under
   * the document's FIRST grid — so without this an additional charge is saved
   * as an item and counted twice.
   */
  readonly section?: string
  /** Rows a `parentPicker` column may choose from, by that column's key. */
  readonly pickable?: Readonly<Record<string, readonly PickableRow[]>>
  /**
   * What the button that appends a row says, from the grid's own definition —
   * "Add a BOQ line", "Add an estimation line", "Add a charge". Every grid
   * declares one; a screen that says "Add a line" on all of them makes the
   * operator work out which of the four grids they are looking at.
   */
  readonly addLabel?: string
  /**
   * A history icon beside each saved line's rate, opening the purchase price
   * history at that line (PUR-039).
   */
  readonly onRateHistory?: (lineId: string) => void
  /**
   * The row "Add" appends, when the grid is one of two over a section. Its
   * seq is then the caller's (unique across the section) and removing a row
   * leaves the others' seqs alone, so two grids cannot hand out the same one.
   */
  readonly newRow?: () => LineRow
  /**
   * "Add" opens the new row in the row dialog (`LineGridDef.addOpensDialog`)
   * instead of leaving a blank row in the grid — BOQ Detail, Material Info.
   */
  readonly addOpensDialog?: boolean
  /** The row dialog's title (`LineGridDef.detailTitle`). */
  readonly detailTitle?: string
  /** The cell the row dialog asks before the rest (`LineGridDef.detailAsksFirst`). */
  readonly detailAsksFirst?: string
  /** The automatic reference, drawn above that first question (`LineGridDef.autoRef`). */
  readonly detailRef?: string
  /** What a new row starts with (`LineGridDef.rowDefaults`). */
  readonly rowDefaults?: Readonly<Record<string, string>>
  /**
   * Drawn across the grid under a row — a BOQ line's estimate (PROJ1-008).
   * Null draws nothing.
   */
  readonly rowBelow?: (row: LineRow, index: number) => ReactNode
  /** What the empty grid says (`LineGridDef.emptyText`). */
  readonly emptyText?: string
  /**
   * False where rows come only from the grid's sources (`LineGridDef.pickOnly`):
   * no "Add" button. Rows already there can still be edited and removed.
   */
  readonly canAdd?: boolean
}

/**
 * The choices of an `optionsFrom` cell: the words on the other grid's rows,
 * plus the row's own value when that is not among them — a summary item
 * typed before it had to be one of the list still reads, and can be changed.
 */
export const optionsWithValue = (
  options: readonly PickableRow[], value: string,
): PickableRow[] => (value === '' || options.some((o) => o.value === value)
  ? [...options]
  : [...options, { value, label: `${value} (not on the list)` }])

/**
 * qty x rate less discount, so the operator sees the effect of a keystroke
 * immediately. It is a preview only: the server recalculates on save and the
 * saved response overwrites it, because the server is the authority on money.
 */
export const previewAmount = (row: LineRow): string | null => {
  // Billed in another unit: the billing quantity at the billing rate (PUR-048).
  const billed = asText(row['billingUnit']) !== '' && asDecimalString(row['billingQty']) !== null
    && asDecimalString(row['billingRate']) !== null
  // An adjustment is typed as its amount: one of it, at that figure.
  const q = billed ? asDecimalString(row['billingQty'])
    : asDecimalString(row['quantity']) ?? (row['section'] === 'ADJUSTMENT' ? '1' : null)
  const r = billed ? asDecimalString(row['billingRate']) : asDecimalString(row['rate'])
  if (q === null || r === null) return asDecimalString(row['amount'])
  const d = asDecimalString(row['discountPct']) ?? '0'
  // A two-rate BOQ line, and a Material and Labour estimate, are worth both
  // halves; the server prices them that way and the preview has to agree.
  const half = row['lineType'] === 'SECTION' ? null : asDecimalString(row['supplyRate'])
  try {
    if (row['lineType'] === 'SECTION') return null
    return lineAmount(qty(q), half === null ? rate(r) : addRate(rate(r), rate(half)), pct(d))
  } catch {
    return asDecimalString(row['amount'])
  }
}

const gridTotal = (lines: readonly LineRow[]): string => {
  const amounts: MoneyValue[] = []
  for (const line of lines) {
    const amount = previewAmount(line)
    if (amount === null) continue
    try {
      // An addition among deductions counts the other way, as it does on the
      // server, so the grid's own total is the net effect on the document.
      const sign = line['section'] === 'ADJUSTMENT' && String(line['direction'] ?? '').toUpperCase() !== 'ADDITION' ? -1 : 1
      amounts.push(money(sign === -1 ? `-${amount}`.replace('--', '') : amount))
    } catch {
      // A half-typed rate is not an arithmetic error; it simply does not count yet.
    }
  }
  return amounts.length === 0 ? ZERO_MONEY : addMoney(...amounts)
}

/** Cells that name a unit, offered from the Units master (PUR-048: the billing unit too). */
const UNIT_KEYS: ReadonlySet<string> = new Set(['unit', 'billingUnit'])

const isNumeric = (column: LineColumnDef): boolean =>
  column.type === 'qty' || column.type === 'rate' || column.type === 'money'

/**
 * A formula cell's value, from the row's own figures; null while an operand
 * is still missing or half-typed.
 */
export const formulaValue = (formula: NonNullable<LineColumnDef['formula']>, row: LineRow): string | null => {
  try {
    if ('subtract' in formula) {
      const [from, less] = formula.subtract
      const a = asText(row[from])
      if (a === '') return null
      const result = subQty(qty(a), qty(asText(row[less]) || '0'))
      // Floored only where it has to be: a shortfall on a count is negative.
      return formula.floor !== undefined && Number(result) < Number(formula.floor) ? qty(formula.floor) : result
    }
    if ('product' in formula) {
      const [a, b] = formula.product.map((k) => asText(row[k]))
      if (!a || !b) return null
      return lineAmount(qty(a), rate(b))
    }
    const parts = formula.sum.map((k) => asText(row[k])).filter((v) => v !== '')
    return parts.length === 0 ? null : addQty(...parts.map((v) => qty(v)))
  } catch {
    // Half-typed; it will count once it is a number.
    return null
  }
}

const BADGE_TONES = {
  bad: { color: 'var(--bad)', background: 'var(--bad-soft)', borderColor: 'var(--bad-line)' },
  warn: { color: 'var(--warn, #8a5a00)', background: 'var(--warn-soft, #fff4dc)', borderColor: 'var(--warn-line, #f0d9a8)' },
  good: { color: 'var(--good, #1d6b3a)', background: 'var(--good-soft, #e6f4ea)', borderColor: 'var(--good-line, #b9dfc5)' },
} as const

const staticValue = (column: LineColumnDef, value: unknown) => {
  const tone = column.badges?.[asText(value)]
  if (tone) {
    return (
      <span
        className="cell-badge"
        style={{ ...BADGE_TONES[tone], border: '1px solid', borderRadius: 999, padding: '2px 10px', font: '600 13px/1.4 var(--font-body)' }}
      >
        {asText(value)}
      </span>
    )
  }
  // A fixed choice reads as its label, not its stored code.
  if (column.options) {
    const option = column.options.find((o) => o.value === asText(value))
    if (option) return option.label
  }
  if (column.type === 'qty') return <span className="num">{formatQuantity(value)}</span>
  if (column.type === 'date') return asText(value) === '' ? null : formatDate(asText(value))
  if (column.type === 'rate') return <span className="num">{formatRate(value)}</span>
  if (column.type === 'money') {
    const decimal = asDecimalString(value)
    return decimal === null ? null : <Money value={decimal} />
  }
  return asText(value)
}

/**
 * Is this cell open for typing on this row, given what the row says — its
 * `editable`, `visibleWhen`, `editableWhen` and `lockedWhen` (form-layout.ts,
 * which the save check shares).
 */
export const cellEditable = (column: LineColumnDef, row: LineRow): boolean => isLineCellOpen(column, row)

/** A column head, with the asterisk a required cell carries. */
export const columnHeader = (column: LineColumnDef): string => (column.required ? `${column.label} *` : column.label)

/** The master a lookup cell searches on this row. */
const lookupEntity = (column: LineColumnDef, row: LineRow): string =>
  (column.lookupBy ? column.lookupBy.entities[asText(row[column.lookupBy.column])] : undefined)
  ?? column.lookup?.entity ?? 'item'

const isRateOnly = (row: LineRow): boolean => asText(row['rateOnly']).toUpperCase() === 'YES'

/**
 * Long text clamped to a few lines with "more" — a BOQ narrative runs to
 * paragraphs and a read-only grid of them is a page per row (reference).
 */
export const Clamped = ({ text, lines }: { text: string; lines: number }) => {
  const [open, setOpen] = useState(false)
  const long = text.length > lines * 60 || text.split('\n').length > lines
  return (
    <span className="cell-secondary">
      <span
        style={open || !long ? undefined : {
          display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}
      >
        {text}
      </span>
      {long ? (
        <button type="button" className="link-button" onClick={() => setOpen((o) => !o)}>
          {open ? 'less' : '…more'}
        </button>
      ) : null}
    </span>
  )
}

export const LineGrid = ({
  columns, lines, readOnly, onChange, addLabel, section, pickable, newRow, onRateHistory, addOpensDialog = false, detailTitle,
  detailAsksFirst, detailRef, rowDefaults, rowBelow, emptyText, canAdd = true,
}: LineGridProps) => {
  /** Which rows have their quantity's source opened (PUR-039). */
  const [sourceOpen, setSourceOpen] = useState<Record<string, boolean>>({})
  const patch = (index: number, key: string, value: string) => patchMany(index, { [key]: value })
  // Reference: a line opened as a form (Line #n of N), and rows ticked for a bulk action.
  const [detailAt, setDetailAt] = useState<number | null>(null)
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set())

  /**
   * Several cells of one row in one change. Two `patch` calls in a row both
   * start from the same `lines`, so the second silently undid the first —
   * which is how picking a material could keep its name and lose its id.
   */
  const patchMany = (index: number, values: Readonly<Record<string, string>>) => {
    onChange(lines.map((line, i) => (i === index ? withFormulas({ ...line, ...values }, Object.keys(values)) : line)))
  }

  /**
   * Cells that are arithmetic on two others, kept in step as either is typed
   * — a receipt's accepted quantity is delivered less rejected. The server
   * works it out again on save; this keeps the row and its amount honest
   * until then.
   */
  const withFormulas = (row: LineRow, changed: readonly string[]): LineRow => {
    let next = row
    for (const column of columns) {
      const formula = column.formula
      if (!formula) continue
      try {
        const operands = 'subtract' in formula ? formula.subtract : 'product' in formula ? formula.product : formula.sum
        if (!operands.some((k) => changed.includes(k))) continue
        const value = formulaValue(formula, next)
        if (value !== null) next = { ...next, [column.key]: value }
      } catch {
        // Half-typed; it will count once it is a number.
      }
    }
    return next
  }

  // The Units master, for a unit cell someone types into. Only fetched when a
  // grid actually lets a unit be typed.
  const api = useApi()
  const unitsListId = useId()
  const unitEditable = !readOnly && columns.some((c) => UNIT_KEYS.has(c.key) && c.editable === true)
  // The company's GST slabs, for a Tax cell: its name, and the picker.
  const slabs = useGstSlabs(columns.some((c) => c.taxSlab === true))
  const units = useQuery({
    queryKey: ['units', api.companyId],
    queryFn: () => api.masterList('unit', { pageSize: '200' }),
    enabled: unitEditable,
    staleTime: 5 * 60_000,
  })

  const removeMany = (indexes: ReadonlySet<number>) => {
    const kept = lines.filter((_, i) => !indexes.has(i))
    onChange(newRow ? kept : kept.map((line, i) => ({ ...line, seq: i + 1 })))
    setSelected(new Set())
  }
  const remove = (index: number) => removeMany(new Set([index]))

  const add = () => {
    onChange([
      ...lines,
      {
        seq: lines.length + 1,
        quantity: '',
        rate: '',
        description: '',
        ...(section ? { section } : {}),
        ...(rowDefaults ?? {}),
        ...(newRow ? newRow() : {}),
      },
    ])
    // The reference's "+ Add" is a form for the new line, not a blank row.
    if (addOpensDialog) setDetailAt(lines.length)
  }

  const tableColumns = useMemo<TableColumn<LineRow>[]>(() => {
    // A secondary column is printed under the cell before it, not in its own.
    const groups: { column: LineColumnDef; secondaries: LineColumnDef[] }[] = []
    // A dialog-only column is a field of the row dialog, not a grid column.
    for (const column of columns.filter((c) => c.dialogOnly !== true)) {
      const previous = groups[groups.length - 1]
      if (column.secondary && previous) previous.secondaries.push(column)
      else groups.push({ column, secondaries: [] })
    }

    const built: TableColumn<LineRow>[] = groups.map(({ column, secondaries }) => {
      const spec: TableColumn<LineRow> = { key: column.key, header: columnHeader(column) }
      if (column.width !== undefined) spec.width = column.width
      if (column.align !== undefined) spec.align = column.align
      else if (isNumeric(column)) spec.align = 'right'

      spec.render = (row) => {
        const index = lines.indexOf(row)
        const open = !readOnly && cellEditable(column, row)
        const main =
          // Not asked on this row (`visibleWhen`): the cell reads empty, or
          // the cell it names in its place (`elseShow`, PUR-004).
          !isLineCellVisible(column, row) ? (column.elseShow ? <span>{asText(row[column.elseShow])}</span> : null)
          // Not a question on this row (`dashWhen`): a lump sum's quantity.
          : isLineCellDashed(column, row) ? <span className="num">-</span>
          : column.key === 'seq' ? (
            <span className="num">{index + 1}</span>
          ) : column.key === 'amount' ? (
            (() => {
              const amount = previewAmount(row)
              return amount === null ? null : <Money value={amount} />
            })()
          ) : column.type === 'qty' && !open && isRateOnly(row) ? (
            // A rate-only line has no quantity; the reference prints RO.
            <span className="num">RO</span>
          ) : column.taxSlab && !open ? (
            // "GST @ 18%", or the bare rate where no slab matches it.
            <span>{slabText(slabs, row, column)}</span>
          ) : column.key === 'itemId' && !open ? (
            // An id is never shown to a person. The name comes from the pull
            // or from the server's own resolution; the id is the last resort.
            <span>{asText(row['itemLabel']) || asText(row['itemName']) || asText(row['itemId'])}</span>
          ) : !open ? (
            // A looked-up id reads as what it is called, never as the id; a
            // formula cell a picker filled in without it reads as its sum.
            column.lookup && asText(row[`${column.key}Label`]) !== ''
              ? <span>{asText(row[`${column.key}Label`])}</span>
              : column.clamp && readOnly && asText(row[column.key]) !== ''
                ? <Clamped text={asText(row[column.key])} lines={column.clamp} />
                : staticValue(column, column.formula && ('product' in column.formula || asText(row[column.key]) === '')
                  ? formulaValue(column.formula, row) : row[column.key])
          ) : column.key === 'itemId' ? (
            // An id column is never typed by hand. Before this, adding a line
            // meant pasting a uuid into a text box.
            <Lookup
              key={lookupEntity(column, row)}
              entity={lookupEntity(column, row)}
              value={asText(row['itemId'])}
              label={asText(row['itemLabel']) || undefined}
              placeholder={`Search ${({ charge: 'charges', labour: 'labour items', expense_head: 'lumpsum heads' } as Record<string, string>)[lookupEntity(column, row)] ?? 'materials'}`}
              onChange={(value, option) => {
                const next: Record<string, string> = { itemId: value }
                if (option) {
                  next['itemLabel'] = option.label
                  // What the material master says, unless the line already
                  // says something else: a unit and an HSN code typed by hand
                  // are the operator's to keep.
                  const unit = option.extra?.['unit']
                  const hsn = option.extra?.['hsnSacCode']
                  if (unit && !asText(row['unit'])) next['unit'] = unit
                  if (hsn && !asText(row['hsnSacCode'])) next['hsnSacCode'] = hsn
                  // Another item brings its own Tax: a slab picked for the
                  // last one no longer applies.
                  const tax = option.extra?.['taxPct']
                  if (tax !== undefined && tax !== null) next['taxPct'] = tax
                  if (asText(row['taxRateId']) !== '') next['taxRateId'] = ''
                }
                patchMany(index, next)
              }}
            />
          ) : UNIT_KEYS.has(column.key) ? (
            <input
              className="line-input"
              aria-label={column.label}
              list={unitsListId}
              value={asText(row[column.key])}
              onChange={(e) => patch(index, column.key, e.currentTarget.value)}
            />
          ) : column.taxSlab ? (
            // One of the company's GST slabs; the rate every total reads follows it.
            <Select
              value={slabOf(slabs, row, column)?.id ?? ''}
              onChange={(value) => patchMany(index, slabPatch(slabs, column, value))}
              placeholder={slabText(slabs, row, column) || 'Not set'}
              options={slabs.map((s) => ({ value: s.id, label: s.label }))}
            />
          ) : column.optionsFrom ? (
            // One of another grid's rows, by its words: a Billing Summary Item.
            <Select
              value={asText(row[column.key])}
              onChange={(value) => patch(index, column.key, value)}
              placeholder="Not chosen"
              options={optionsWithValue(pickable?.[column.key] ?? [], asText(row[column.key]))}
            />
          ) : column.parentPicker ? (
            // A row of another grid on this document: the BOQ line an estimate
            // costs, the billing term a BOQ line is billed under.
            <Select
              value={asText(row[column.key])}
              onChange={(value) => patch(index, column.key, value)}
              placeholder="Not chosen"
              options={[...(pickable?.[column.key] ?? [])]}
            />
          ) : column.options ? (
            // A fixed-choice cell. Typing MATERIAL by hand into a text box is
            // how one cost type ends up spelt four different ways.
            <Select
              value={asText(row[column.key])}
              onChange={(value) => patchMany(index, { [column.key]: value, ...(column.sets?.[value] ?? {}) })}
              placeholder="Not set"
              options={[...column.options]}
            />
          ) : column.lookup ? (
            (() => {
              // Scoped by another cell of the same row — a participant's
              // contact among its supplier's contacts (PUR-020).
              const owners = column.lookup.scope
                ? lookupOwners({ lookup: { entity: column.lookup.entity, scope: column.lookup.scope } }, row)
                : undefined
              return (
                <Lookup
                  entity={column.lookup.entity}
                  value={asText(row[column.key])}
                  label={asText(row[`${column.key}Label`]) || undefined}
                  placeholder={`Search ${column.label.toLowerCase()}`}
                  {...(owners ? { params: { owners } } : {})}
                  {...(owners === null ? { emptyMessage: 'Choose the supplier first' } : {})}
                  onChange={(value, option) => {
                    const next: Record<string, string> = { [column.key]: value }
                    if (option) {
                      next[`${column.key}Label`] = option.label
                      for (const [from, to] of Object.entries(column.lookup?.fills ?? {})) {
                        const carried = option.extra?.[from]
                        if (column.lookup?.refill) next[to] = carried ?? ''
                        else if (carried && !asText(row[to])) next[to] = carried
                      }
                    }
                    patchMany(index, next)
                  }}
                />
              )
            })()
          ) : (
            <input
              className={isNumeric(column) ? 'line-input line-input--right num' : 'line-input'}
              inputMode={isNumeric(column) ? 'decimal' : undefined}
              // A need-by date, an incurred-on date: the browser's own picker.
              type={column.type === 'date' ? 'date' : undefined}
              aria-label={column.label}
              title={column.hint}
              value={asText(row[column.key])}
              onChange={(e) => patch(index, column.key, e.currentTarget.value)}
            />
          )

        // Where a quantity came from, and what a rate has been before (PUR-039).
        const rowKey = asText(row['id']) || `line-${index}`
        const source = column.key === 'quantity' && asText(row['sourceLabel']) !== '' ? asText(row['sourceLabel']) : null
        const history = column.key === 'rate' && onRateHistory && asText(row['id']) !== '' ? asText(row['id']) : null
        // The receipt a transfer line was taken from, as a mark on its quantity (STK-005).
        const lot = column.lotNote && asText(row[column.lotNote]) !== '' ? asText(row[column.lotNote]) : null
        const lotKey = `${rowKey}:${column.key}`
        const icons = source || history || lot ? (
          <span className="cell-icons">
            {lot ? (
              <button type="button" className="icon-button" aria-label={`Lot of ${column.label.toLowerCase()}`}
                title={lot} aria-expanded={sourceOpen[lotKey] === true}
                onClick={() => setSourceOpen((open) => ({ ...open, [lotKey]: !open[lotKey] }))}>
                <Icon name="box" size={16} />
              </button>
            ) : null}
            {source ? (
              <button type="button" className="icon-button" aria-label="Where this quantity came from"
                title={source} aria-expanded={sourceOpen[rowKey] === true}
                onClick={() => setSourceOpen((open) => ({ ...open, [rowKey]: !open[rowKey] }))}>
                <Icon name="layers" size={16} />
              </button>
            ) : null}
            {history ? (
              <button type="button" className="icon-button" aria-label="Purchase price history of this line"
                title="Purchase price history" onClick={() => onRateHistory?.(history)}>
                <Icon name="chart" size={16} />
              </button>
            ) : null}
          </span>
        ) : null

        return (
          <>
            {main}
            {icons}
            {lot && sourceOpen[lotKey] ? <span className="cell-secondary">Lot {lot}</span> : null}
            {source && sourceOpen[rowKey] ? (
              <span className="cell-secondary">
                From {source}
                {asDecimalString(row['sourceQty']) !== null ? ` · ${formatQuantity(row['sourceQty'])} asked` : ''}
              </span>
            ) : null}
            {secondaries.map((s) =>
              readOnly || s.editable !== true ? (
                s.clamp && asText(row[s.key]) !== ''
                  ? <Clamped key={s.key} text={asText(row[s.key])} lines={s.clamp} />
                  : <span className="cell-secondary" key={s.key}>{asText(row[s.key])}</span>
              ) : (
                <input
                  key={s.key}
                  className="line-input line-input--secondary"
                  aria-label={s.label}
                  placeholder={s.label}
                  value={asText(row[s.key])}
                  onChange={(e) => patch(index, s.key, e.currentTarget.value)}
                />
              ),
            )}
          </>
        )
      }
      return spec
    })

    if (!readOnly) {
      built.unshift({
        key: '__select',
        header: '',
        width: 48,
        render: (row) => {
          const i = lines.indexOf(row)
          return (
            <input
              type="checkbox"
              aria-label={`Select line ${i + 1}`}
              checked={selected.has(i)}
              onChange={(e) => {
                const on = e.currentTarget.checked
                setSelected((was) => {
                  const next = new Set(was)
                  if (on) next.add(i); else next.delete(i)
                  return next
                })
              }}
            />
          )
        },
      })
      built.push({
        key: '__remove',
        header: 'Line',
        width: 190,
        render: (row) => (
          <span className="row" style={{ gap: 4 }}>
            <Button kind="ghost" onClick={() => setDetailAt(lines.indexOf(row))}>Open</Button>
            <Button kind="ghost" onClick={() => remove(lines.indexOf(row))}>Remove</Button>
          </span>
        ),
      })
    }
    return built
  }, [columns, lines, readOnly, unitsListId, newRow, selected, sourceOpen, onRateHistory, slabs])

  return (
    <>
      {unitEditable ? (
        <datalist id={unitsListId}>
          {(units.data?.rows ?? []).map((u) => (
            <option key={String(u['id'])} value={asText(u['code'])}>{asText(u['name'])}</option>
          ))}
        </datalist>
      ) : null}
      <Table
        columns={tableColumns}
        rows={lines}
        getKey={(row) => asText(row['id']) || `line-${lines.indexOf(row)}`}
        empty={emptyText ?? 'No lines yet.'}
        stacked
        {...(rowBelow ? { renderBelow: (row: LineRow) => rowBelow(row, lines.indexOf(row)) } : {})}
      />
      <div className="lines-total">
        <span className="label">Lines total</span>
        <Money value={gridTotal(lines)} size={17} />
      </div>
      {readOnly ? null : (
        <div className="row" style={{ marginTop: 16, gap: 8 }}>
          {canAdd ? <Button kind="ghost" onClick={add}>{addLabel ?? 'Add a line'}</Button> : null}
          {selected.size > 0 ? (
            <Button kind="ghost" onClick={() => removeMany(selected)}>
              Remove {selected.size} selected
            </Button>
          ) : null}
        </div>
      )}
      {detailAt !== null && !readOnly && lines[detailAt] ? (
        <LineDetail
          columns={columns}
          lines={lines}
          index={detailAt}
          pickable={pickable}
          slabs={slabs}
          {...(detailTitle ? { title: detailTitle } : {})}
          {...(detailAsksFirst ? { asksFirst: detailAsksFirst } : {})}
          {...(detailRef ? { refColumn: detailRef } : {})}
          onPatch={patchMany}
          onMove={setDetailAt}
          onRemove={(i) => { remove(i); setDetailAt(null) }}
          onClose={() => setDetailAt(null)}
          onAddNext={() => { add(); setDetailAt(lines.length) }}
        />
      ) : null}
    </>
  )
}
